import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLES, ROLE_BADGE } from "@/lib/constants";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  LayoutDashboard, CalendarClock, BedDouble, Database, Users, ScrollText,
  Menu, LogOut, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import AppLogo from "@/components/AppLogo";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin_iprjt", "case_manager", "petugas_admisi"], testid: "nav-dashboard" },
  { to: "/pasien", label: "Jadwal Pasien", icon: CalendarClock, roles: ["super_admin", "admin_iprjt", "case_manager", "petugas_admisi", "rawat_inap"], testid: "nav-pasien" },
  { to: "/antrian", label: "Antrian Kamar", icon: BedDouble, roles: ["super_admin", "admin_iprjt", "case_manager", "petugas_admisi"], testid: "nav-antrian" },
  { to: "/master-data", label: "Master Data", icon: Database, roles: ["super_admin"], testid: "nav-master-data" },
  { to: "/pengguna", label: "Pengguna", icon: Users, roles: ["super_admin"], testid: "nav-pengguna" },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["super_admin"], testid: "nav-audit-log" },
];

// Shows `label` beside the element only while the sidebar is collapsed (the text is hidden then). The trigger is a
// wrapper div on purpose: Radix's asChild merges className as a string, which would break NavLink's function className.
const CollapsedTip = ({ collapsed, label, children }) =>
  collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild><div>{children}</div></TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  ) : children;

// Which items appear is decided only by NAV[].roles; `collapsed` changes how they look, never which ones show.
const NavItems = ({ user, onNavigate, collapsed = false }) => (
  <nav className="flex-1 px-3 py-4 space-y-1">
    {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
      <CollapsedTip key={n.to} collapsed={collapsed} label={n.label}>
        <NavLink
          to={n.to}
          end={n.to === "/"}
          onClick={onNavigate}
          data-testid={n.testid}
          aria-label={collapsed ? n.label : undefined}
          className={({ isActive }) =>
            `flex items-center rounded-lg text-sm font-medium transition-colors duration-150 ${
              collapsed ? "justify-center h-10 w-10 mx-auto" : "gap-3 px-3.5 py-2.5"
            } ${
              isActive
                ? "bg-[#267B84] text-white shadow-sm"
                : "text-slate-600 hover:bg-[#60C0D0]/15 hover:text-[#267B84]"
            }`
          }
        >
          <n.icon className="h-4.5 w-4.5 shrink-0" size={18} />
          {!collapsed && n.label}
        </NavLink>
      </CollapsedTip>
    ))}
  </nav>
);

// The mobile drawer always shows the full brand; the desktop sidebar hides the text when collapsed.
const Brand = ({ collapsed = false }) => (
  <div className={`flex items-center gap-3 py-5 border-b border-slate-100 ${collapsed ? "justify-center" : "px-5"}`}>
    <AppLogo />
    {!collapsed && (
      <div className="min-w-0">
        <p className="font-heading font-bold text-slate-900 leading-tight">TERPADU-OP RSCM</p>
        <p className="text-[11px] text-slate-500 leading-tight">Jadwal Pasien Rawat Inap</p>
      </div>
    )}
  </div>
);

// Round button sitting on the sidebar's right border, level with the logo, in both modes.
const SidebarToggle = ({ collapsed, onToggle }) => {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const label = collapsed ? "Perbesar menu" : "Perkecil menu";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onToggle} aria-label={label} aria-expanded={!collapsed} data-testid="sidebar-toggle"
          className="absolute -right-3.5 top-8 h-7 w-7 rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm flex items-center justify-center hover:text-[#267B84] hover:border-[#60C0D0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B9AD]">
          <Icon size={15} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
};

// Desktop sidebar preference, per browser. Storage can be unavailable (private mode, blocked site data): then it
// simply starts expanded and the toggle still works for the session.
const SIDEBAR_KEY = "terpadu.sidebarCollapsed";
const readCollapsed = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
};
const saveCollapsed = (value) => {
  try {
    window.localStorage.setItem(SIDEBAR_KEY, value ? "1" : "0");
  } catch {
    /* keep the in-memory state only */
  }
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () => setCollapsed((c) => {
    saveCollapsed(!c);
    return !c;
  });
  // Width of the desktop sidebar; the topbar and content are offset by the same amount.
  const sidebarW = collapsed ? "lg:w-[72px]" : "lg:w-64";
  const contentPad = collapsed ? "lg:pl-[72px]" : "lg:pl-64";
  // Antrian Kamar and Jadwal Pasien (wide tables with Diagnosa / Rencana Tindakan) use the whole screen width; other
  // pages keep the cap.
  const fullWidth = ["/antrian", "/pasien"].includes(useLocation().pathname);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar desktop */}
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 ${sidebarW} flex-col bg-white border-r border-slate-200 z-40 transition-[width] duration-200`}
        data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <Brand collapsed={collapsed} />
        <SidebarToggle collapsed={collapsed} onToggle={toggleCollapsed} />
        <NavItems user={user} collapsed={collapsed} />
        <div className={`border-t border-slate-100 ${collapsed ? "py-4 flex justify-center" : "p-4"}`}>
          <CollapsedTip collapsed={collapsed} label={`${user.nama} · ${ROLES[user.role]}`}>
            <div className="flex items-center gap-3" data-testid="sidebar-user" tabIndex={collapsed ? 0 : undefined}
              aria-label={collapsed ? `${user.nama}, ${ROLES[user.role]}` : undefined}>
              <div className="h-9 w-9 rounded-full bg-[#00B9AD]/15 text-[#009E94] flex items-center justify-center font-semibold text-sm shrink-0">
                {(user.nama || user.email).charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{user.nama}</p>
                  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${ROLE_BADGE[user.role]}`}>
                    {ROLES[user.role]}
                  </span>
                </div>
              )}
            </div>
          </CollapsedTip>
        </div>
      </aside>

      {/* Topbar */}
      <header className={`sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 ${contentPad} transition-[padding] duration-200`}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu size={18} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-white">
                <div className="flex flex-col h-full">
                  <Brand />
                  <NavItems user={user} onNavigate={() => setMobileOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="lg:hidden flex items-center gap-2">
              <AppLogo className="h-8 w-8" />
              <span className="font-heading font-bold text-slate-900">TERPADU-OP RSCM</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`hidden sm:inline-block text-xs px-2 py-1 rounded-md border ${ROLE_BADGE[user.role]}`} data-testid="topbar-role-badge">
              {ROLES[user.role]}
            </span>
            <span className="hidden md:block text-sm text-slate-600" data-testid="topbar-user-name">{user.nama}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button" className="gap-1.5">
              <LogOut size={14} />
              <span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </div>
      </header>

      <main className={`${contentPad} transition-[padding] duration-200`}>
        <div className={`p-4 sm:p-6 lg:p-8 ${fullWidth ? "" : "max-w-[1400px]"}`}>
          <Outlet />
        </div>
      </main>
    </div>
    </TooltipProvider>
  );
}
